import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function IconXCloseLight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M17.2929 5.29302C17.6834 4.90249 18.3164 4.90249 18.707 5.29302C19.0975 5.68354 19.0975 6.31655 18.707 6.70708L13.414 12L18.707 17.293L18.7753 17.3692C19.0957 17.762 19.0731 18.341 18.707 18.7071C18.3408 19.0732 17.7618 19.0958 17.3691 18.7754L17.2929 18.7071L11.9999 13.4141L6.70696 18.7071C6.31643 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6835 5.29289 17.293L10.5859 12L5.29289 6.70708L5.22453 6.63091C4.90418 6.23813 4.92678 5.65913 5.29289 5.29302C5.65901 4.9269 6.23801 4.90431 6.63078 5.22466L6.70696 5.29302L11.9999 10.586L17.2929 5.29302Z"
        fill="currentColor"
      />
    </svg>
  );
}

export interface ImageLightboxProps {
  children: React.ReactNode;
  onClose: () => void;
  /** Shown as `aria-label` on the dialog root */
  ariaLabel?: string;
}

/**
 * Full-screen media viewer; portals to `document.body` and locks scroll.
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({ children, onClose, ariaLabel }) => {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    const focusTimer = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [onClose, onKey]);

  return createPortal(
    <div
      className="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || 'Image viewer'}
      onClick={onClose}
    >
      <button
        ref={closeBtnRef}
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="image-lightbox__close"
      >
        <IconXCloseLight className="image-lightbox__close-icon" />
      </button>
      <div className="image-lightbox__frame" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
};

export function MagnifierIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="11" y1="8.5" x2="11" y2="13.5" />
      <line x1="8.5" y1="11" x2="13.5" y2="11" />
      <line x1="16.5" y1="16.5" x2="22" y2="22" />
    </svg>
  );
}

export interface ZoomableImageProps {
  src: string;
  alt?: string;
  /** Added to the outer wrapper (e.g. page-specific width rules) */
  className?: string;
  /** Classes on the thumbnail `<img>` */
  imgClassName?: string;
}

/**
 * Thumbnail that opens a full-screen lightbox on click; magnifier hint on hover.
 */
const ZoomableImage: React.FC<ZoomableImageProps> = ({ src, alt, className, imgClassName }) => {
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const label = alt ? `Open image: ${alt}` : 'Open image';
  const thumbClasses = ['zoomable-image__thumb', imgClassName].filter(Boolean).join(' ');

  return (
    <div className={['zoomable-image', className].filter(Boolean).join(' ')}>
      <button
        type="button"
        onClick={handleOpen}
        aria-label={label}
        className="zoomable-image__trigger"
      >
        <img src={src} alt={alt || ''} className={thumbClasses} loading="lazy" />
        <span className="zoomable-image__mag" aria-hidden>
          <MagnifierIcon />
        </span>
      </button>
      {open ? (
        <ImageLightbox onClose={handleClose} ariaLabel={alt || 'Image viewer'}>
          <img src={src} alt={alt || ''} className="image-lightbox__img" />
        </ImageLightbox>
      ) : null}
    </div>
  );
};

export default ZoomableImage;
